import React, { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import StepContainer from '../../components/StepContainer';
import Button from '../../components/Button';
import { useBuildAudience } from '../../context/BuildAudienceContext';
import { UI_TEXT, ERROR_MESSAGES } from '../../config/ui-text';

interface AudienceDefineProps {
  onNext: () => void;
  onBack?: () => void;
}

const AudienceDefine: React.FC<AudienceDefineProps> = ({
  onNext,
  onBack
}) => {
  const { buildAudienceData, updateBuildAudienceData } = useBuildAudience();

  const [companyUrl, setCompanyUrl] = useState(buildAudienceData.websiteUrl || '');
  const [isValid, setIsValid] = useState(true);

  // URL-only validation
  const validateUrl = (url: string) => {
    if (!url.trim()) {
      setIsValid(false);
      return;
    }

    try {
      // Add protocol if missing for URL validation
      const urlToTest = url.startsWith('http') ? url : `https://${url}`;
      const urlObj = new URL(urlToTest);

      // Must have a valid hostname with at least one dot
      const isValidUrl = urlObj.hostname.includes('.') && urlObj.hostname.length > 3;
      setIsValid(isValidUrl);
    } catch {
      setIsValid(false);
    }
  };

  const handleInputChange = (value: string) => {
    setCompanyUrl(value);
    validateUrl(value);
  };

  const handleContinue = () => {
    if (isValid && companyUrl.trim()) {
      // Ensure URL has protocol
      let finalUrl = companyUrl.trim();
      if (!finalUrl.startsWith('http')) {
        finalUrl = `https://${finalUrl}`;
      }

      // Save to context
      updateBuildAudienceData({
        websiteUrl: finalUrl,
      }, 1); // Step 1: websiteUrl input

      onNext();
    }
  };

  return (
    <StepContainer
      title=""
      className="animate-fadeIn flex items-center justify-center min-h-[70vh]"
    >
      <div className="max-w-3xl mx-auto space-y-8 w-full">
        <div className="space-y-6">
          {/* Main Question */}
          <div className="space-y-2">
            <h1 className="text-2xl font-normal text-gray-900 leading-relaxed">
              {UI_TEXT.TITLES.COMPANY_INPUT_QUESTION}
            </h1>
            <p className="text-base font-light text-gray-400 leading-relaxed">
              {UI_TEXT.SUBTITLES.COMPANY_INPUT_DESC}
            </p>
          </div>

          {/* Input Field */}
          <div className="space-y-3">
            <input
              type="text"
              className={`w-full px-0 py-2 bg-transparent border-0 border-b-2 transition-all duration-200 text-2xl font-light placeholder:text-sm placeholder-gray-300 outline-none ring-0 focus:ring-0 focus:outline-none ${
                companyUrl && isValid
                  ? 'border-b-primary'
                  : companyUrl && !isValid
                  ? 'border-b-red-400'
                  : 'border-b-gray-200 hover:border-b-gray-300'
              } focus:border-b-primary`}
              placeholder={UI_TEXT.PLACEHOLDERS.COMPANY_INPUT}
              value={companyUrl}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValid) {
                  handleContinue();
                }
              }}
            />

            {/* Validation Message */}
            {companyUrl && !isValid && (
              <p className="text-sm font-light text-red-400">
                {ERROR_MESSAGES.INVALID_URL}
              </p>
            )}
          </div>
        </div>

        {/* Navigation buttons - right below the input */}
        <div className="flex items-center space-x-4">
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={!isValid || !companyUrl.trim()}
            className="font-light"
            withArrow={false}
          >
            {UI_TEXT.BUTTONS.CONTINUE}
          </Button>
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-gray-400 hover:text-gray-600 font-light"
          >
            {UI_TEXT.BUTTONS.BACK}
          </Button>
        </div>
      </div>
    </StepContainer>
  );
};

export default AudienceDefine;